<template>
  <section>
    <FlashMessage ref="flashMessage" />

    <div class="search-filter">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="Search products..."
      />
    </div>

    <div class="product-grid">
      <LoadingIndicator v-if="loading" message="Loading products..." />

      <div v-else-if="filteredProducts.length === 0" class="no-data-message">
        No products found.
      </div>

      <h4>Shop</h4>

      <div class="products">
        <div
          class="product-card"
          v-for="product in filteredProducts"
          :key="product.id"
        >
          <img :src="getFullImageUrl(product.thumbnail)" :alt="product.name" />
          <h3>{{ product.name }}</h3>

          <div class="price">
            <span class="original-price">Tsh: {{ product.price }}</span>
            <span class="selling-price">Tsh: {{ product.discount_price }}</span>
          </div>
          <div class="location">
            <span class="selling-price"> + Tsh: {{product.delivery_fee ?? 0.0}} Delivery</span>
          </div>

          <div class="rating">
            <span v-for="star in 5" :key="star">⭐</span>
          </div>

          <div class="rating">
            <h3>{{ product.agent.agent_name }}</h3>
          </div>

          <div class="location">
            <i class="fas fa-map-marker-alt"></i>
            <span>{{ product.location ?? 'HQ'}}</span>
          </div>

          <p class="description">
            {{ truncateDescription(product.description) }}
          </p>

          <div class="action-buttons">
            <button class="icon-button" title="Add to Cart">
              <i class="fas fa-cart-plus"></i>
            </button>
            <button
              class="icon-button"
              title="View Details"
              @click="ViewProductDetails(product.id)"
            >
              <i class="fas fa-eye"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import LoadingIndicator from "@/components/Loading_indicator.vue";
import { fetchProducts, API_BASE_URL } from "@/API-requests/Requests";
import FlashMessage from "@/components/FlashMessage.vue";

const router = useRouter();

const products = ref([]);
const loading = ref(true);
const error = ref(null);
const searchQuery = ref("");
const flashMessage = ref(null);

onMounted(async () => {
  try {
    const productsData = await fetchProducts();
    products.value = productsData;
    flashMessage.value.showFlashMessage(
      "success",
      "Welcome, our shop is ready!"
    );
  } catch (err) {
    console.error("Error fetching products:", err);
    error.value = "Failed to load products. Please try again later.";
    flashMessage.value.showFlashMessage("failure", error.value);
  } finally {
    loading.value = false;
  }
});

const getFullImageUrl = (imagePath) => {
  return `${API_BASE_URL}${imagePath}`;
};

const filteredProducts = computed(() => {
  return products.value.filter((product) =>
    product.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const truncateDescription = (description) => {
  const maxLength = 40;
  return description.length > maxLength
    ? description.slice(0, maxLength) + "..."
    : description;
};

const ViewProductDetails = (id) => {
  router.push(`/product-details/${id}`);
};
</script>
<style scoped>
.search-filter {
  position: fixed;
  top: 4rem;
  left: 290px;
  right: 20px;
  z-index: 999;
  background-color: #fff;
  padding: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

.search-filter input {
  width: 100%;
  padding: 10px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.product-grid {
  flex: 1;
  margin-left: 280px;
  padding-top: 0px;
}

.products {
  display: grid;
  grid-template-columns: repeat(4, 1fr); /* 4 items per row on large screens */
  gap: 20px;
  padding: 20px;
}

.product-card {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.product-card img {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
}

.product-card h3 {
  font-size: 18px;
  margin: 10px 0;
  color: #333;
}

.price {
  margin: 10px 0;
}

.original-price {
  text-decoration: line-through;
  color: #999;
  margin-right: 10px;
}

.selling-price {
  font-weight: bold;
  color: #42b983;
}

.rating {
  margin: 10px 0;
  color: #ffc107;
}

.location {
  margin: 10px 0;
  color: #555;
  font-size: 14px;
}

.location i {
  margin-right: 5px;
}

.description {
  font-size: 14px;
  color: #666;
  margin: 10px 0;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
}

.icon-button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: #555;
  transition: color 0.3s ease;
}

.icon-button:hover {
  color: rgb(215, 55, 55);
}

.icon-button i {
  pointer-events: none;
}

/* No Data Message */
.no-data-message {
  text-align: center;
  font-size: 18px;
  color: #666;
  margin-top: 20px;
}

@media (max-width: 1200px) {
  .products {
    grid-template-columns: repeat(
      3,
      1fr
    );
  }
}

@media (max-width: 992px) {
  .products {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
.product-grid {
  margin-left: 0;
  padding-top: 60px;
  
  /* controlling left stretch of cards*/
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  box-sizing: border-box;
  padding-left: 16px;
  padding-right: 16px;
}
  .search-filter {
    left: 40px;
    right: 5px;
    top: 60px;
  }

  .products {
    grid-template-columns: repeat(
      2,
      1fr
    );
  }
}

@media (max-width: 480px) {
  .products {
    grid-template-columns: 1fr;
  }
}
</style>
